import React, { useCallback } from 'react';
import { Modal, Button, Typography, Table, Badge } from '@crisellevaldez/speculo-ui';
import { ExternalLink, Check, Download, RotateCw } from 'lucide-react';
import { Organization } from '../../../types/organization.types';
import {
  downloadDatabaseUploadErrorFile,
  useDatabaseUploadRequests,
  useUpdateDatabaseUploadStatus,
  DatabaseUploadRequest,
} from '../../../hooks/useDatabaseUploadRequests';

interface DatabaseUploadRequestsModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedOrg: Organization | undefined;
  onSuccess?: () => void;
}

type Column = {
  key: string;
  header: string;
  isCentered?: boolean;
  cell: (request: DatabaseUploadRequest) => React.ReactNode;
};

export const DatabaseUploadRequestsModal: React.FC<DatabaseUploadRequestsModalProps> = ({
  isOpen,
  onClose,
  selectedOrg,
  onSuccess,
}) => {
  const { data: requests = [], isLoading, refetch } = useDatabaseUploadRequests(selectedOrg?.id);
  const [updatingId, setUpdatingId] = React.useState<string | null>(null);
  const [downloadingErrorId, setDownloadingErrorId] = React.useState<string | null>(null);

  const updateStatusMutation = useUpdateDatabaseUploadStatus();

  const handleRefresh = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const handleStatusUpdate = (
    requestId: string,
    newStatus: 'pending' | 'processing' | 'complete'
  ) => {
    if (!selectedOrg?.id) return;

    setUpdatingId(requestId);
    updateStatusMutation.mutate(
      {
        requestId,
        newStatus,
        orgId: selectedOrg.id,
        currentUploads:
          (selectedOrg.databaseUploads as Array<{ id: string; status: string }>) || [],
      },
      {
        onSuccess: () => {
          setUpdatingId(null);
          if (onSuccess) {
            onSuccess();
          }
        },
        onError: () => {
          setUpdatingId(null);
        },
      }
    );
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString();
  };

  const handleErrorFileDownload = async (request: DatabaseUploadRequest) => {
    if (!selectedOrg?.id || !request.errorFilePath) return;

    setDownloadingErrorId(request.id);
    try {
      await downloadDatabaseUploadErrorFile(
        selectedOrg.id,
        request.id,
        request.errorFileName || `database-upload-errors-${request.id}.csv`
      );
    } finally {
      setDownloadingErrorId(null);
    }
  };

  const columns: Column[] = [
    {
      key: 'userEmail',
      header: 'Submitted By',
      cell: (request) => (
        <div className="truncate max-w-[150px]" title={request.userEmail}>
          {request.userEmail}
        </div>
      ),
    },
    {
      key: 'createdAt',
      header: 'Date',
      cell: (request) => formatDate(request.createdAt),
    },
    {
      key: 'aiOptedIn',
      header: 'AI Opted-In',
      isCentered: true,
      cell: (request) => (
        <div className="flex justify-center">
          {request.aiOptedIn === 'yes' ? (
            <Check className="h-4 w-4 text-green-600 dark:text-green-400" />
          ) : (
            <span className="text-gray-500 dark:text-gray-400">No</span>
          )}
        </div>
      ),
    },
    {
      key: 'flowTypes',
      header: 'Flow Type(s)',
      cell: (request) => {
        const flowTypes = request.flowTypes || [];
        if (flowTypes.length === 0) return '-';
        return (
          <div className="flex flex-wrap gap-1">
            {flowTypes.map((type, index) => (
              <Badge
                key={index}
                variant={type === 'engage' ? 'success' : type === 'revive' ? 'warning' : 'info'}
                className={
                  type === 'engage'
                    ? 'dark:bg-green-900/30 dark:text-green-300'
                    : type === 'revive'
                      ? 'dark:bg-amber-900/30 dark:text-amber-300'
                      : 'dark:bg-blue-900/30 dark:text-blue-300'
                }
              >
                {type}
              </Badge>
            ))}
          </div>
        );
      },
    },
    {
      key: 'assignment',
      header: 'Assignment',
      cell: (request) => {
        if (!request.useAssignment || !request.assignmentAction) return '-';
        return (
          <Badge
            variant={request.assignmentAction === 'assign' ? 'success' : 'warning'}
            className={
              request.assignmentAction === 'assign'
                ? 'dark:bg-green-900/30 dark:text-green-300'
                : 'dark:bg-amber-900/30 dark:text-amber-300'
            }
          >
            {request.assignmentAction === 'assign' ? 'Assign' : 'Unassigned'}
          </Badge>
        );
      },
    },

    {
      key: 'actions',
      header: 'Actions',
      cell: (request) => (
        <div className="flex items-center gap-2">
          <a
            href={request.fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="text-yellow-600 hover:text-yellow-700"
            title="View File"
          >
            <ExternalLink className="w-4 h-4" />
          </a>
          {request.errorFilePath && (
            <button
              type="button"
              onClick={() => handleErrorFileDownload(request)}
              disabled={downloadingErrorId === request.id}
              className="text-red-600 hover:text-red-700 disabled:opacity-50"
              title="Download Error File"
              aria-label="Download Error File"
            >
              <Download className="w-4 h-4" />
            </button>
          )}
          {request.status !== 'complete' && (
            <Button
              size="sm"
              variant="primary"
              onClick={() => handleStatusUpdate(request.id, 'complete')}
              isLoading={updatingId === request.id}
              disabled={updatingId === request.id}
            >
              Mark Complete
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl">
      <Modal.Header>
        <Typography variant="h3">Database Upload Requests</Typography>
      </Modal.Header>

      <Modal.Body className="py-8">
        <div className="h-[300px] 3xl:h-[500px]">
          <Table<DatabaseUploadRequest>
            data={requests}
            columns={columns}
            keyExtractor={(item) => item.id}
            emptyStateText="No database upload requests found"
            loading={isLoading}
          />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button
          variant="outline"
          size="md"
          onClick={handleRefresh}
          disabled={isLoading}
          className="flex items-center gap-2"
        >
          <RotateCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
